import { Trans } from "@lingui/macro";
import { apiTokenJwtUpdate, apiTokens } from "@smp/api";
import {
  jwtDecode,
  REACT_APP__ENVIRONMENT,
  REACT_APP__URL_SMP_WS,
  REACT_APP__WS_CONNECTION_TIMEOUT,
  REACT_APP__WS_RECONNECT_ATTEMPTS_LEGAL,
  REACT_APP__WS_RECONNECT_DELAY,
  REACT_APP__WS_TOKEN_REQUEST_ATTEMPTS_LEGAL,
} from "@smp/utils";
import { proxy, Remote, wrap } from "comlink";
import produce from "immer";
import { nanoid } from "nanoid";
import { useSnackbar } from "notistack";
import { PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useUpdateEffect } from "react-use";

import { getMqPemissions } from "@/screens/app/context/ws/ws.static";

import { useContextApp } from "../app";
import { WebStompWorker } from "./worker";
import MyWorker from "./worker/worker?worker";
import { ContextWs } from "./ws.context";
import { IContextWs, IMqPermissions, ITask } from "./ws.types";
import { convertMessageDataToTask } from "./ws.utils";
import { useWsSubscriptions } from "./ws_subscriptions";
import { useWsTaskHandlers } from "./ws_task_handlers";

// worker is non-reactive and needs to be created only once
let webStompWorker: undefined | Remote<WebStompWorker>;

// tokenUpdateAttempts counter is non-reactive as it is used for scheduleUpdateToken callback
// which calls itself automatically by timeout which otherwise might "lock" to component's previous state
let tokenUpdateAttempts: number = 0;

let hadErrors = false;

const log: typeof console.log = (...args) => {
  if (["smmplanner.io", "production"].includes(REACT_APP__ENVIRONMENT)) {
    return;
  }

  console.log(...args);
};

export const ProviderWebStomp = ({ children }: PropsWithChildren<{}>) => {
  const { enqueueSnackbar } = useSnackbar();

  const {
    state: { me, organizationId },
  } = useContextApp();

  const taskHandlers = useWsTaskHandlers();
  const [tasks, setTasks] = useState<Map<string, ITask>>(new Map());
  const [taskListeners, setTaskListeners] = useState<Map<ITask["handler"], Array<(message: any) => void>>>(new Map());
  const [jwt, setJwt] = useState<string | undefined>(undefined);
  const [mqPermissions, setMqPermissions] = useState<IMqPermissions | undefined>(undefined);
  const subscribtions = useWsSubscriptions(mqPermissions);
  const tokenUpdateTimeoutId = useRef(0);

  // connectionAttempts are placed in state
  // because worker's config depends on them and should be updated reactively
  const [connectionAttempts, setConnectionAttempts] = useState(0);

  const meId = useMemo(() => me.id, [me]);

  /*
    Create worker only if it exists
  */
  const ensuredWebStompWorker = useMemo(() => {
    if (!webStompWorker) {
      webStompWorker = wrap<WebStompWorker>(new MyWorker());
    }

    return webStompWorker;
  }, []);

  const reconnectDelay = useMemo(
    () => REACT_APP__WS_RECONNECT_DELAY * Math.pow(2, Math.max(connectionAttempts - 1, 0)),
    [connectionAttempts],
  );

  const handleConnectionLoss = useCallback(() => {
    enqueueSnackbar(
      <Trans>
        Lost connection to wss://mq.smp.io. Some features may work incorrectly. Try refreshing the page, and if problem
        persists, contact our support team.
      </Trans>,
      { persist: true, variant: "warning" },
    );

    hadErrors = true;
  }, [enqueueSnackbar]);

  const handleConnectionRestoration = useCallback(() => {
    hadErrors &&
      enqueueSnackbar(<Trans>Connection to wss://mq.smp.io has been successfully restored.</Trans>, {
        persist: true,
        variant: "success",
      });

    hadErrors = false;
  }, [enqueueSnackbar]);

  const updateToken = useCallback(async () => {
    const jwt = await apiTokenJwtUpdate();
    setJwt(jwt);
  }, []);

  const scheduleUpdateToken = useCallback(async () => {
    if (tokenUpdateAttempts - 1 && !((tokenUpdateAttempts - 1) % REACT_APP__WS_TOKEN_REQUEST_ATTEMPTS_LEGAL)) {
      handleConnectionLoss();
    }

    window.clearTimeout(tokenUpdateTimeoutId.current);

    tokenUpdateTimeoutId.current = window.setTimeout(
      async () => {
        try {
          await updateToken();
        } catch (failureOrFailureApi) {
          scheduleUpdateToken();
        }
        tokenUpdateAttempts += 1;
      },
      REACT_APP__WS_RECONNECT_DELAY * Math.pow(2, tokenUpdateAttempts),
    );
  }, [handleConnectionLoss, updateToken]);

  const onError = useCallback(
    (e) => {
      if (e.code === "WS_AUTH_ERROR") {
        scheduleUpdateToken();
      }
    },
    [scheduleUpdateToken],
  );

  const handleMessage = useCallback<NonNullable<WebStompWorker["onMessage"]>>((messageData) => {
    const task = convertMessageDataToTask(messageData);
    if (!task) {
      return;
    }

    setTasks((messages) =>
      produce(messages, (draft) => {
        draft.set(nanoid(), task);
      }),
    );
  }, []);

  useEffect(() => {
    if (!meId) {
      return;
    }

    if (!apiTokens.smp) {
      scheduleUpdateToken();
    } else {
      setJwt(apiTokens.smp);
    }
  }, [meId, scheduleUpdateToken]);

  const addTaskListener = useCallback<IContextWs["addTaskListener"]>((name, callback) => {
    setTaskListeners((state) => {
      state.set(name, [...(state.get(name) || []), callback]);

      return state;
    });

    return () => {
      setTaskListeners((state) => {
        state.set(
          name,
          (state.get(name) || []).filter((existingCallback) => existingCallback !== callback),
        );

        return state;
      });
    };
  }, []);

  useEffect(() => {
    if (!meId) {
      return;
    }

    if (!apiTokens.smp) {
      scheduleUpdateToken();
    } else {
      setJwt(apiTokens.smp);
    }
  }, [meId, scheduleUpdateToken]);

  /**
   * Enable required options for webStompWorker
   */

  useEffect(() => {
    ensuredWebStompWorker.updateOptions({
      enableLog: false,
    });
  }, [ensuredWebStompWorker]);

  /**
   * Update jwt and activate client (if required)
   */
  useEffect(() => {
    const worker = ensuredWebStompWorker;

    (async () => {
      await worker.clearSubscribtions();

      if (!jwt || jwtDecode(jwt).sub === null) {
        // If jwt disappeared deactivate worker
        await worker.deactivate();
        return;
      }

      const permissions = getMqPemissions(jwt);

      await worker.updateConfig({
        brokerURL: REACT_APP__URL_SMP_WS,
        connectHeaders: {
          login: "jwt/" + jwt,
          password: "",
        },
        connectionTimeout: REACT_APP__WS_CONNECTION_TIMEOUT,
        discardWebsocketOnCommFailure: true,
        heartbeatIncoming: 25000,
        heartbeatOutgoing: 25000,
        reconnectDelay,
      });

      if (!(await worker.active)) {
        await worker.activate();
      }

      setMqPermissions(permissions);
    })();
  }, [ensuredWebStompWorker, jwt, reconnectDelay]);

  useEffect(() => {
    const worker = ensuredWebStompWorker;

    (async () => {
      await worker.updateOnMessage(proxy(handleMessage));
    })();
  }, [ensuredWebStompWorker, handleMessage]);

  useEffect(() => {
    const worker = ensuredWebStompWorker;

    (async () => {
      await worker.updateClient(
        "onConnect",
        proxy(() => {
          setConnectionAttempts(0);
          tokenUpdateAttempts = 0;
          handleConnectionRestoration();
        }),
      );

      await worker.updateClient(
        "beforeConnect",
        proxy(() => {
          setConnectionAttempts((current) => {
            const result = current + 1;
            if (result - 1 && !((result - 1) % REACT_APP__WS_RECONNECT_ATTEMPTS_LEGAL)) {
              worker.deactivate();
              handleConnectionLoss();
            }

            return result;
          });
        }),
      );
    })();
  }, [ensuredWebStompWorker, handleConnectionLoss, handleConnectionRestoration]);

  /**
   * Update error hanlder if it changed
   */
  useEffect(() => {
    const worker = ensuredWebStompWorker;

    (async () => {
      await worker.updateOnError(proxy(onError));
    })();
  }, [ensuredWebStompWorker, onError]);

  /**
   * Update subscribtions if they changed
   */
  useEffect(() => {
    const worker = ensuredWebStompWorker;

    (async () => {
      await worker.updateSubscribtions(subscribtions);
    })();

    return () => {
      (async () => {
        if (worker) {
          worker.clearSubscribtions();
        }
      })();
    };
  }, [subscribtions, ensuredWebStompWorker]);

  useEffect(() => {
    if (!tasks.size) {
      return;
    }

    tasks.forEach((task, key) => {
      log(task.handler, task.body);

      taskHandlers[task.handler]?.(task.body);

      taskListeners.get(task.handler)?.forEach((callback) => {
        callback(task.body);
      });

      setTasks((tasks) =>
        produce(tasks, (draft) => {
          draft.delete(key);
        }),
      );
    });
  }, [taskHandlers, taskListeners, tasks]);

  useUpdateEffect(() => {
    apiTokenJwtUpdate().then(setJwt);
  }, [organizationId]);

  return (
    <ContextWs.Provider
      value={{
        addTaskListener: addTaskListener,
      }}
    >
      {children}
    </ContextWs.Provider>
  );
};
